import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import { TableFooter } from "@/components/Table/TableFooter";
import type { PageInfo, PageSize, PaginationVariables } from "@/components/Pagination/Pagination";

type LocalConnectionPaginationProps = {
  pageInfo: PageInfo;
  setVariables: (variables: PaginationVariables) => void;
  pageSize: PageSize;
  setPageSize: (pageSize: PageSize) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageRows: number;
  totalRows: number;
};

export default function LocalConnectionPagination({
  pageInfo,
  setVariables,
  pageSize,
  setPageSize,
  currentPage,
  setCurrentPage,
  pageRows,
  totalRows
}: LocalConnectionPaginationProps) {
  const startPage = totalRows === 0 ? 0 : currentPage * pageSize + 1;
  const endPage = totalRows === 0 ? 0 : startPage + pageRows - 1;
  const pageSizeOptions: PageSize[] = [20, 50, 100];

  const pagination = (
    <div className="flex items-start md:items-center justify-end flex-col md:flex-row sticky">
      <div className="hidden sm:flex items-center mr-6">
        <span className="text-label-large mr-1">Rows per page:</span>
        <div className="w-[68px]">
          <div className="relative">
            <select
              className="bg-white border text-sys-brand-on-surface-variant appearance-none text-label-large rounded-lg block w-full h-8 py-1 px-2 pl-4 border-sys-brand-on-surface-variant cursor-pointer"
              onChange={(event) => {
                const nextPageSize = Number(event.target.value) as PageSize;
                setPageSize(nextPageSize);
                setCurrentPage(0);
                setVariables({ first: nextPageSize });
              }}
              data-testid="assignments-page-size-select"
              value={String(pageSize)}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <Icon name="arrow_drop_down" size={24} className="pointer-events-none absolute bottom-1 right-2" />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center md:justify-end w-full md:w-fit gap-4">
        <div className="text-label-large">
          {startPage} - {endPage} of {totalRows}
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            name="navigate_before"
            variant="square"
            onClick={() => {
              if (!pageInfo.startCursor) return;
              setCurrentPage(currentPage - 1);
              setVariables({ before: pageInfo.startCursor, last: pageSize });
            }}
            disabled={!pageInfo.hasPreviousPage || !pageInfo.startCursor}
            data-testid="assignments-previous-button"
          />
          <IconButton
            name="navigate_next"
            variant="square"
            onClick={() => {
              if (!pageInfo.endCursor) return;
              setCurrentPage(currentPage + 1);
              setVariables({ after: pageInfo.endCursor, first: pageSize });
            }}
            disabled={!pageInfo.hasNextPage || !pageInfo.endCursor}
            data-testid="assignments-next-button"
          />
        </div>
      </div>
    </div>
  );

  return <TableFooter pagination={pagination} />;
}
